// components/layout/ChatColumn/ToolStatusIndicator.jsx
import React, { useMemo } from 'react';
import {
  Zap, Database, Search, Calculator, Globe, Clock, Cloud, Mail,
  Calendar, FileText, Brain, CheckCircle2, AlertTriangle, Loader2,
  Image, GitBranch, XCircle, Shield, Users, ScanSearch, Grid, CheckSquare,
} from 'lucide-react';

/* ========================
 * toolRegistry - Central configuration for known, specific tools
 * Provides precise, non-ambiguous theming and text for each tool.
 * NOTE: MCP tools are now handled dynamically by getToolConfig.
 * ======================== */
const toolRegistry = {
  'Search_The_Web': {
    icon: <Search size={16} />,
    displayName: 'Web Search',
    statusText: {
      pending: 'Searching the web...',
      processing: 'Searching the web...',
      success: 'Web Search Complete',
      error: 'Web Search Failed',
      interrupted: 'Web Search Cancelled',
    },
    colors: { bg: 'from-emerald-900/80 to-emerald-950/90', border: 'border-emerald-500/40', text: 'text-emerald-200', iconBg: 'bg-emerald-900/80' }
  },
  'Explore_Websites': {
    icon: <Globe size={16} />,
    displayName: 'Website Explorer',
    statusText: {
      pending: 'Exploring website...',
      processing: 'Exploring website...',
      success: 'Website Exploration Complete',
      error: 'Website Exploration Failed',
      interrupted: 'Exploration Cancelled',
    },
    colors: { bg: 'from-teal-900/80 to-teal-950/90', border: 'border-teal-500/40', text: 'text-teal-200', iconBg: 'bg-teal-900/80' }
  },
  'Workspace_Brain': {
    icon: <Database size={16} />,
    displayName: 'Workspace Brain',
    isLongRunning: true,
    statusText: {
      pending: 'Reading workspace brain...',
      processing: 'Reading workspace brain...',
      success: 'Workspace Brain Read',
      error: 'Workspace Brain Read Failed',
      interrupted: 'Workspace Brain Read Cancelled',
    },
    colors: { bg: 'from-purple-900/80 to-purple-950/90', border: 'border-purple-500/40', text: 'text-purple-200', iconBg: 'bg-purple-900/80' }
  },
  'Send_Email_To_User': {
    icon: <Mail size={16} />,
    displayName: 'Email',
    statusText: {
      pending: 'Preparing email...',
      processing: 'Sending Email...',
      success: 'Email Sent',
      error: 'Failed to Send Email',
      interrupted: 'Email Cancelled',
    },
    colors: { bg: 'from-blue-900/80 to-blue-950/90', border: 'border-blue-500/40', text: 'text-blue-200', iconBg: 'bg-blue-900/80' }
  },
  'Generate_Image': {
    icon: <Image size={16} />,
    displayName: 'Image Generation',
    statusText: {
      pending: 'Generating image...',
      processing: 'Generating image...',
      success: 'Image Generated',
      error: 'Image Generation Failed',
      interrupted: 'Generation Cancelled',
    },
    colors: { bg: 'from-pink-900/80 to-pink-950/90', border: 'border-pink-500/40', text: 'text-pink-200', iconBg: 'bg-pink-900/80' }
  },
  'My_Workspaces': {
    icon: <GitBranch size={16} />,
    displayName: '@MyWorkspaces',
    isLongRunning: true,
    statusText: {
      pending: 'Accessing workspace...',
      processing: 'Workspace task running...',
      success: 'Workspace Task Complete',
      error: 'Workspace Task Failed',
      interrupted: 'Workspace Task Cancelled',
    },
    colors: { bg: 'from-gray-800/80 to-black/90', border: 'border-gray-500/40', text: 'text-gray-200', iconBg: 'bg-gray-900/80' }
  },
  'Shelbula_Coder': {
    icon: <Cloud size={16} />,
    displayName: '@Coder',
    isLongRunning: true,
    statusText: {
      pending: 'Initializing @Coder...',
      processing: '@Coder is working...',
      success: '@Coder is Complete',
      error: '@Coder call failed...',
      interrupted: '@Coder interrupted...',
    },
    colors: { bg: 'from-blue-900/80 to-blue-950/90', border: 'border-blue-500/40', text: 'text-blue-200', iconBg: 'bg-blue-900/80' }
  },
  'Admin': {
    icon: <Shield size={16} />,
    displayName: 'Admin Control',
    isLongRunning: true,
    statusText: {
      pending: 'Sending admin command...',
      processing: 'Admin command processing...',
      success: 'Admin Command Successful',
      error: 'Admin Command Failed',
      interrupted: 'Admin Command Cancelled',
    },
    colors: { bg: 'from-red-900/80 to-red-950/90', border: 'border-red-500/40', text: 'text-red-200', iconBg: 'bg-red-900/80' }
  },
  'Task_Swarm': {
    icon: <Users size={16} />,
    displayName: 'Task Swarm',
    isLongRunning: true,
    statusText: {
      pending: 'Dispatching task swarm...',
      processing: 'Swarm is working on the task...',
      success: 'Swarm Task Complete',
      error: 'Swarm Task Failed',
      interrupted: 'Swarm Task Cancelled',
    },
    colors: { bg: 'from-indigo-900/80 to-indigo-950/90', border: 'border-indigo-500/40', text: 'text-indigo-200', iconBg: 'bg-indigo-900/80' }
  },
  'Detect_And_Label': {
    icon: <ScanSearch size={16} />,
    displayName: 'Image Detection',
    statusText: {
      pending: 'Analyzing image...',
      processing: 'Detecting objects in image...',
      success: 'Image Analysis Complete',
      error: 'Image Analysis Failed',
      interrupted: 'Analysis Cancelled',
    },
    colors: { bg: 'from-cyan-900/80 to-cyan-950/90', border: 'border-cyan-500/40', text: 'text-cyan-200', iconBg: 'bg-cyan-900/80' }
  },
  'Claude_Web_Search': {
    icon: <Search size={16} />,
    displayName: 'Claude Web Search',
    statusText: {
      pending: 'Searching the web (Claude)...',
      processing: 'Searching the web (Claude)...',
      success: 'Claude Web Search Complete',
      error: 'Claude Web Search Failed',
      interrupted: 'Claude Web Search Cancelled',
    },
    colors: { bg: 'from-orange-900/80 to-orange-950/90', border: 'border-orange-500/40', text: 'text-orange-200', iconBg: 'bg-orange-900/80' }
  },
};

/* ========================
 * Default Configurations - Fallbacks for different tool types
 * ======================== */
const defaultToolConfig = {
  icon: <Zap size={16} />,
  displayName: 'General Tool',
  isLongRunning: false,
  statusText: {
    pending: 'Executing...',
    processing: 'Processing...',
    success: 'Complete!',
    error: 'Failed',
    interrupted: 'Cancelled',
  },
  colors: { bg: 'from-sky-900/80 to-sky-950/90', border: 'border-sky-500/40', text: 'text-sky-200', iconBg: 'bg-sky-900/80' }
};

const mcpDefaultConfig = {
  icon: <Brain size={16} />,
  isLongRunning: false,
  statusText: {
    pending: 'Accessing MCP...',
    processing: 'MCP action running...',
    success: 'MCP Action Complete',
    error: 'MCP Action Failed',
    interrupted: 'MCP Action Cancelled',
  },
  colors: { bg: 'from-fuchsia-900/80 to-fuchsia-950/90', border: 'border-fuchsia-500/40', text: 'text-fuchsia-200', iconBg: 'bg-fuchsia-900/80' }
};


/* ========================
 * getToolConfig - Retrieves tool configuration with dynamic MCP handling
 * ======================== */
const getToolConfig = (toolName) => {
  if (!toolName) {
    return defaultToolConfig;
  }

  // Priority 1: Exact match in the registry for specific overrides.
  if (toolRegistry[toolName]) {
    return toolRegistry[toolName];
  }

  // Priority 2: Dynamic check for any tool following the MCP naming convention.
  if (toolName.endsWith('_MCP_Server')) {
    const displayName = toolName.replace(/_MCP_Server$/, '').replace(/_/g, ' ') + ' MCP';
    return {
      ...mcpDefaultConfig,
      displayName: displayName,
    };
  }
  
  // Priority 3: Fallback for any other unregistered tool.
  return {
    ...defaultToolConfig,
    displayName: toolName.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()),
  };
};

/* ========================
 * getArgsMeta - Extract a concise human-readable metadata string from tool args.
 * Returns null if nothing meaningful is available.
 * ======================== */
const truncate = (str, max = 60) => {
  if (!str || typeof str !== 'string') return null;
  const trimmed = str.trim();
  return trimmed.length > max ? trimmed.slice(0, max - 1) + '…' : trimmed;
};

const getArgsMeta = (toolName, toolArgs) => {
  if (!toolArgs || typeof toolArgs !== 'object') return null;
  const name = (toolName || '').toLowerCase();

  if (name === 'terminal') {
    return toolArgs.command ? { label: truncate(toolArgs.command, 80), mono: true } : null;
  }
  if (name === 'addnewfile' || name === 'readfile' || name === 'deletefile') {
    return toolArgs.filePath ? { label: truncate(toolArgs.filePath, 60) } : null;
  }
  if (name === 'renamefile') {
    const from = toolArgs.oldPath ? truncate(toolArgs.oldPath, 28) : null;
    const to = toolArgs.newPath ? truncate(toolArgs.newPath, 28) : null;
    if (from && to) return { label: `${from} → ${to}` };
    return from ? { label: from } : null;
  }
  if (name === 'searchfiles') {
    return toolArgs.pattern ? { label: `"${truncate(toolArgs.pattern, 50)}"` } : null;
  }
  if (name === 'listfiles') {
    return toolArgs.directoryPath ? { label: truncate(toolArgs.directoryPath, 60) } : null;
  }
  if (name === 'loadskill' || name === 'addskill') {
    return toolArgs.name ? { label: truncate(toolArgs.name, 60) } : null;
  }
  return null;
};

/* ========================
 * ToolStatusIndicator - Elegant animated tool status display
 * ======================== */
const ToolStatusIndicator = ({
  toolName,
  status: originalStatus = 'pending',
  className = '',
  resultPreview,
  statusMessageOverride,
  toolArgs,
}) => {
  const toolConfig = useMemo(() => getToolConfig(toolName), [toolName]);
  const argsMeta = useMemo(() => getArgsMeta(toolName, toolArgs), [toolName, toolArgs]);

  let status = originalStatus;
  const isMcpTool = toolName && toolName.endsWith('_MCP_Server');

  // ARCHITECTURAL FIX: Correctly interpret backend signals based on tool type.
  if (isMcpTool && status === 'interrupted') {
    // For MCP tools, 'interrupted' means the tool call was successfully dispatched
    // and the backend stream for this tool is complete. We show 'success'.
    status = 'success';
  } else if (toolConfig.isLongRunning && status === 'interrupted') {
    // For other *truly* long-running tools, 'interrupted' means the initial
    // stream is done, but the task continues in the background.
    status = 'processing';
  }

  const baseContainerClasses = "flex items-center gap-2.5 w-full text-xs px-3 py-2 rounded-lg border";
  const isProcessingOrPending = ['pending', 'processing'].includes(status);

  let style = {};
  let icon = toolConfig.icon;
  let currentStatusText = '';

  if (isProcessingOrPending) {
    style = {
      backgroundColor: 'rgba(var(--info-color-rgb), 0.07)',
      borderColor: 'rgba(var(--info-color-rgb), 0.25)',
      color: 'rgb(var(--info-color-rgb))',
    };
    icon = <Loader2 size={14} className="animate-spin flex-shrink-0" />;
    currentStatusText = statusMessageOverride || toolConfig.statusText[status] || 'Working...';
  } else if (status === 'success') {
    style = {
      backgroundColor: 'rgba(var(--success-rgb), 0.07)',
      borderColor: 'rgba(var(--success-rgb), 0.25)',
      color: 'rgb(var(--success-rgb))',
    };
    icon = <CheckCircle2 size={14} className="flex-shrink-0" />;
    currentStatusText = statusMessageOverride || toolConfig.statusText.success;
  } else if (status === 'error') {
     style = {
      backgroundColor: 'rgba(var(--error-rgb), 0.07)',
      borderColor: 'rgba(var(--error-rgb), 0.25)',
      color: 'rgb(var(--error-rgb))',
    };
    icon = <AlertTriangle size={14} className="flex-shrink-0" />;
    currentStatusText = statusMessageOverride || toolConfig.statusText.error;
  } else if (status === 'interrupted') {
    // This now only applies to non-MCP tools that are explicitly cancelled.
    style = {
      backgroundColor: 'rgba(var(--cancelled-rgb), 0.1)',
      borderColor: 'rgba(var(--cancelled-rgb), 0.25)',
      color: 'rgba(var(--cancelled-rgb), 0.9)',
    };
    icon = <XCircle size={14} className="flex-shrink-0" />;
    currentStatusText = statusMessageOverride || toolConfig.statusText.interrupted;
  } else {
    return null;
  }

  return (
    <div className={className}>
      <div className={baseContainerClasses} style={style}>
        {icon}
        <div className="flex-1 min-w-0">
          <span className="font-medium">{currentStatusText}</span>
          {argsMeta && (
            <div className={`mt-0.5 text-[10px] opacity-70 truncate ${argsMeta.mono ? 'font-mono' : ''}`}>
              {argsMeta.label}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ToolStatusIndicator;